<template>
  <div class="schedule">
    <ReportDraftWatermark v-if="report.is_draft"/>
    <h2 class="formTitle">Schedule L: Loans</h2>
    <v-divider></v-divider>
    <div class="report-section">
      <h3 class="line-item-title">
        1. Loans received:
      </h3>
      <div v-if="arrayHasItems(report.user_data.loan.receipts)">
        <v-simple-table dense>
          <thead>
          <tr>
            <th class="dateColumn">
              Date loaned
            </th>
            <th class="typeColumn">
                Type
            </th>
            <th class="paddedColumn">
              Lender/Occupation & Employment
            </th>
            <th class="paddedColumn">
              Amount of loan
            </th>
            <th class="paddedColumn">
              Terms
            </th>
            <th class="paddedColumn">
              Description
            </th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(item, index) in report.user_data.loan.receipts" :key="index">
            <td>
              {{ formatDate(item.date) }}
            </td>
            <td class="typeColumn">
              {{setLoanType(item)}}
            </td>
            <td style="padding-left: 1%">
              <ContactInfo :contacts="contacts" :cont-id="item.contact_key"/>
              <div>
                <RecipientInfo v-if="item.amount > 250" :contacts="contacts" :cont-id="item.contact_key"/>
              </div>
              <div v-for="(ed, endIdx) in item.endorser" :key="`${index}-${endIdx}`">
                <EndorserInfo :contacts="contacts" :cont-id="ed.contact_key"/>
              </div>
              <div>
                <strong>Total contributions:</strong>
                {{ formatMoney(getAggregateTotal(contacts, item.contact_key, item.election)) }}
              </div>
            </td>
            <td class="paddedColumn">
              {{ electionFilter(item.election) }}
              <br/>
              {{ formatMoney(item.amount) }}
            </td>
            <td class="paddedColumn">
              <div v-if="item.interest_rate">
                <strong>Interest rate:</strong><br/>
                {{ item.interest_rate }}<span v-if="item.interest_rate">%</span>
              </div>
              <div v-if="item.due_date">
                <strong>Due date:</strong><br/>
                {{ formatDate(item.due_date) }}
              </div>
              <div>
                <strong>Payment Schedule:</strong><br/>
                {{ item.payment_schedule }}
              </div>
            </td>
            <td class="paddedColumn">
              <div v-if="item.description">
                {{ item.description }}
              </div>
            </td>
          </tr>
          </tbody>
        </v-simple-table>
        <div v-if="hasCarryForward" style="font-size: x-small">&nbsp;&nbsp;* denotes carryforward funds from a previous
          campaign
        </div>
      </div>
      <div v-else class="noReport">
        No loans received this period
      </div>
      <v-divider></v-divider>
    </div>
    <div class="report-section">
      <h3 class="line-item-title">
        2. Loan payments
      </h3>
      <div v-if="arrayHasItems(principle_paid)">
        <v-simple-table dense>
          <thead>
          <tr>
            <th class="dateColumn">
              Date Paid
            </th>
            <th>
              Lender
            </th>
            <th class="text-right">
              Principal Paid
            </th>
            <th class="text-right">
              Interest Paid
            </th>
            <th class="text-right">
              Total Payment
            </th>
            <th class="text-right">
              Balance Owed
            </th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(item, index) in principle_paid" :key="index">
            <td>
              {{ formatDate(item.date) }}
            </td>
            <td>
              <ContactInfo :contacts="contacts" :cont-id="item.contact_key"/>
            </td>
            <td class="amountColumn">
              {{ formatMoney(item.principle_paid) }}
            </td>
            <td class="amountColumn">
              {{ formatMoney(item.interest_paid) }}
            </td>
            <td class="amountColumn">
              {{ formatMoney(item.principle_paid + item.interest_paid) }}
            </td>
            <td class="amountColumn">
              {{ formatMoney(item.balance) }}
            </td>
          </tr>
          </tbody>
        </v-simple-table>
      </div>
      <div v-else class="noReport">
        No loans payments reported this period
      </div>
      <v-divider></v-divider>
    </div>
    <div class="report-section">
      <h3 class="line-item-title">
        3. Loans forgiven:
      </h3>
      <div v-if="arrayHasItems(loans_forgiveness)">
        <v-simple-table dense>
          <thead>
          <tr>
            <th class="dateColumn">
              Date
            </th>
            <th>
              Lender
            </th>
            <th class="text-right">
              Original Amount
            </th>
            <th class="text-right">
              Principal Repaid
            </th>
            <th class="text-right">
              Amount Forgiven
            </th>
            <th>
              Balance Owed
            </th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(item, index) in loans_forgiveness" :key="index">
            <td>
              {{ formatDate(item.date) }}
            </td>
            <td>
              <ContactInfo :contacts="contacts" :cont-id="item.contact_key"/>
            </td>
            <td class="amountColumn">
              {{ formatMoney(item.original_amount) }}
            </td>
            <td class="amountColumn">
              {{ formatMoney(item.principle_paid) }}
            </td>
            <td class="amountColumn">
              {{ formatMoney(item.forgiven_amount) }}
            </td>
            <td class="amountColumn">
              {{ formatMoney(item.original_amount - item.forgiven_amount - item.principle_paid) }}
            </td>
          </tr>
          </tbody>
        </v-simple-table>
      </div>
      <div v-else class="noReport">
        No loans forgiven reported this period
      </div>
      <v-divider></v-divider>
    </div>
    <div class="report-section">
      <h3 class="line-item-title">
        4. Loans still owed
      </h3>
      <div v-if="arrayHasItems(report.user_data.loan.balances)">
        <v-simple-table dense>
          <thead>
          <tr>
            <th class="dateColumn">
              Date
            </th>
            <th>
              Lender
            </th>
            <th class="text-right">
              Original Amount
            </th>
            <th class="text-right">
              Principal Repaid or Forgiven
            </th>
            <th class="text-right">
              Amount Owed
            </th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(item, index) in report.user_data.loan.balances" :key="index">
            <td>
              {{ formatDate(item.date) }}
            </td>
            <td>
              <ContactInfo :contacts="contacts" :cont-id="item.contact_key"/>
            </td>
            <td class="amountColumn">
              {{ formatMoney(item.original_amount) }}
            </td>
            <td class="amountColumn">
              {{ formatMoney(item.principle_paid) }}
            </td>
            <td class="amountColumn">
              {{ formatMoney(differenceOwed(item.original_amount, item.principle_paid)) }}
            </td>
          </tr>
          </tbody>
        </v-simple-table>
      </div>
      <div v-else class="noReport">
        No loans stilled owed reported this period
      </div>
      <v-divider></v-divider>
    </div>
  </div>
</template>
<script>
import filters from "../filters";
import utils from "./report-utils";
import ContactInfo from "./ContactInfo";
import RecipientInfo from "./RecipientInfo";
import EndorserInfo from "./EndorserInfo";
import ReportDraftWatermark from "@/components/ReportDraftWatermark.vue";

export default {
  name: "ScheduleL",
  components: {ReportDraftWatermark, EndorserInfo, RecipientInfo, ContactInfo},
  props: {
    report: Object
  },
  data() {
    return {
      loans_forgiveness: null,
      principle_paid: null,
      hasCarryForward: false,
    }
  },
  methods: {
    ...filters.filters, ...utils,
    differenceOwed(original, repaid) {
      return original - repaid
    },
    displayLoanPayments() {
      this.principle_paid = this.report.user_data.loan.payments.filter(obj => {
        if (obj.hasOwnProperty('principle_paid') && !obj.hasOwnProperty('forgiven_amount')) {
          return obj.principle_paid;
        }
      })
    },
    displayLoanForgiveness() {
      this.loans_forgiveness = this.report.user_data.loan.payments.filter(obj => {
        return obj.forgiven_amount;
      })
    },
    setLoanType(item) {
      let loan_type = ''
      if (item.in_kind) {
          loan_type = "In-kind "
      }  else {
          loan_type = "Cash "
      }
      if(item.carry_forward) {
        this.hasCarryForward = true
        return loan_type + '*'
      }
      return loan_type;
    }
  },
  computed: {
    contacts() {
      return this.report.user_data.contacts;
    },
  },
  created() {
    if (this.report.user_data.loan) {
      this.sortByDate(this.report.user_data.loan.receipts);
      this.sortByDate(this.report.user_data.loan.payments);
      this.sortByDate(this.report.user_data.loan.balances);
    }

    this.displayLoanForgiveness();
    this.displayLoanPayments();
  }
}
</script>

<style scoped>
.schedule { position: relative; }
</style>
